<script setup>
import { computed, ref, watch } from 'vue'

const props = defineProps({
    pageSize: {
        type: Number,
        default: 20,
    },
    currentPage: {
        type: Number,
        default: 0,
    },
    pageCount: {
        type: Number,
        default: 0,
    },
    itemCount: {
        type: Number,
        default: 0,
    },
})

/**
 * The items per page.
 *
 * @type {Array}
 */
const items = [10, 20, 50, 100, 250]

/**
 * The component's emitted events.
 */
const emits = defineEmits(['pageSizeChanged', 'pageChanged'])

/**
 * The local refs.
 */
const size = ref(props.pageSize)

/**
 * Go to the specified page.
 *
 * @param   {Number} pageNumber
 * @return  {void}
 */
const goToPage = (pageNumber) => {
    emits('pageChanged', pageNumber)
}

/**
 * The first item displayed.
 *
 * @return {Number}
 */
const firstItemDisplayed = computed(() => {
    if (props.currentPage > 0) {
        return props.currentPage * props.pageSize
    }

    if (props.itemCount > 0) {
        return 1
    }

    return 0
})

/**
 * The total items displayed.
 *
 * @return {Number}
 */
const totalItemsDisplayed = computed(() => {
    if (props.itemCount > props.pageSize) {
        return (props.currentPage + 1) * props.pageSize
    }

    return props.itemCount
})

/**
 * Watch the size value for changes.
 */
watch(size, () => {
    emits('pageSizeChanged', size.value)
})
</script>

<template>
    <div
        class="ag-grid-custom-pagination-select flex items-center py-2 px-2 bg-white border-t sticky bottom-0"
    >
        <v-btn
            class="me-2 rounded-lg text-gray-600"
            variant="tonal"
            size="x-small"
            icon="far fa-chevrons-left"
            :disabled="props.currentPage === 0"
            @click="goToPage(0)"
        ></v-btn>
        <v-btn
            class="me-2 rounded-lg text-gray-600"
            variant="tonal"
            size="x-small"
            icon="far fa-chevron-left"
            :disabled="props.currentPage === 0"
            @click="goToPage(props.currentPage - 1 || 0)"
        ></v-btn>
        <span class="me-2">
            Page
            <strong>{{
                props.pageCount > 0 ? props.currentPage + 1 : 0
            }}</strong>
            of
            <strong>{{ props.pageCount }}</strong>
        </span>
        <v-btn
            class="me-2 rounded-lg text-gray-600"
            variant="tonal"
            size="x-small"
            icon="far fa-chevron-right"
            :disabled="props.currentPage === props.pageCount - 1"
            @click="goToPage(props.currentPage + 1)"
        ></v-btn>
        <v-btn
            class="me-2 rounded-lg text-gray-600"
            variant="tonal"
            size="x-small"
            icon="far fa-chevrons-right"
            :disabled="props.currentPage === props.pageCount - 1"
            @click="goToPage(props.pageCount)"
        ></v-btn>
        <div class="mr-4">
            <strong>{{ firstItemDisplayed }}</strong>
            to
            <strong>{{ totalItemsDisplayed }}</strong>
            of
            <strong>{{ props.itemCount }}</strong>
        </div>
        <div class="ml-auto">
            <v-select
                label="Page Size"
                hide-details
                :items="items"
                single-line
                menu-icon=""
                v-model="size"
                variant="solo"
                flat
                class=""
            >
                <template v-slot:append-inner>
                    <div
                        class="flex items-center text-base font-semibold text-black"
                    >
                        <v-icon
                            icon="fa fa-chevron-down"
                            size="x-small"
                        ></v-icon>
                    </div>
                </template>
                <template #selection="{ item }">
                    <span class="text-base font-bold text-black">
                        {{ item.title }} <span class="">per page</span>
                    </span>
                </template>
            </v-select>
        </div>
    </div>
</template>
<style lang="scss" src="@/sass/components/_ag-grid-custom.scss"></style>
